import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, Building2, Package, Sparkles, CheckCircle, ExternalLink, DollarSign, Users, TrendingUp } from 'lucide-react';
import { cn } from '../utils/cn';

function TreeNode({ children, label, icon: Icon, level = 0, defaultOpen = false, meta, link }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const hasChildren = children && children.length > 0;

  return (
    <div className={cn("", level > 0 && "ml-6 border-l border-border pl-4")}>
      <button
        onClick={() => hasChildren && setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-start gap-3 py-2 text-left hover:bg-muted/50 rounded-lg px-2 transition-colors",
          hasChildren && "cursor-pointer"
        )}
      >
        {hasChildren ? (
          <span className="mt-0.5 text-muted-foreground">
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        ) : (
          <span className="w-4" />
        )}
        {Icon && <Icon className={cn("w-5 h-5 mt-0.5 flex-shrink-0", level === 0 ? "text-secondary" : "text-muted-foreground")} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("font-medium", level === 0 ? "text-primary" : "text-foreground")}>
              {label}
            </span>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-secondary hover:text-secondary/80"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
          {meta && (
            <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
              {meta.description && <p className="w-full">{meta.description}</p>}
              {meta.founders && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {meta.founders}
                </span>
              )}
              {meta.funding && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  {meta.funding}
                </span>
              )}
              {meta.revenue && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {meta.revenue}
                </span>
              )}
              {meta.pricing && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  {meta.pricing}
                </span>
              )}
            </div>
          )}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TreeView({ research }) {
  const getCapabilityById = (id) => research.capabilities.find(c => c.id === id);
  const getFeatureById = (id) => research.features.find(f => f.id === id);

  return (
    <div className="bg-card rounded-xl p-6 shadow-card space-y-2">
      {research.companies.map((company) => (
        <TreeNode
          key={company.id}
          label={company.name}
          icon={Building2}
          level={0}
          defaultOpen={research.companies.length <= 5}
          link={company.url}
          meta={{
            description: company.description,
            founders: company.founders,
            funding: company.funding,
            revenue: company.revenue,
          }}
        >
          {company.products.map((product) => (
            <TreeNode
              key={product.id}
              label={product.name}
              icon={Package}
              level={1}
              meta={{
                description: product.description,
                pricing: product.pricing,
              }}
            >
              {/* Capabilities */}
              {product.capabilityIds.length > 0 && (
                <TreeNode label="Capabilities" icon={Sparkles} level={2} defaultOpen>
                  {product.capabilityIds.map((capId) => {
                    const capability = getCapabilityById(capId);
                    if (!capability) return null;
                    return (
                      <TreeNode
                        key={capId}
                        label={capability.name}
                        level={3}
                        meta={{ description: capability.description }}
                      />
                    );
                  })}
                </TreeNode>
              )}
              {/* Features */}
              {product.featureIds.length > 0 && (
                <TreeNode label="Features" icon={CheckCircle} level={2} defaultOpen>
                  {product.featureIds.map((featId) => {
                    const feature = getFeatureById(featId);
                    if (!feature) return null;
                    return (
                      <TreeNode
                        key={featId}
                        label={feature.name}
                        level={3}
                        meta={{ description: feature.description }}
                      />
                    );
                  })}
                </TreeNode>
              )}
            </TreeNode>
          ))}
        </TreeNode>
      ))}
    </div>
  );
}
